/**
 * Pure helpers for the SVG renderer's "Colour by" panel control. Kept DOM-free
 * and decoupled from phylocanvas so they can be unit-tested in isolation: the
 * panel glue in `app.ts` derives the value→hex map from `TreeMetadataSync`, then
 * leans on these helpers to reshape it for the renderer and to build the legend.
 */

/**
 * Treat blank and common placeholder tokens as "no value", mirroring
 * `TreeMetadataSync.isEmptyValue`, so a leaf with missing data is left at its base
 * fill rather than painted a sentinel colour.
 */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  const str = String(value).trim().toLowerCase();
  return str === 'null' || str === 'n/a' || str === 'na' || str === '%null%';
}

/**
 * Reshape a `value → hex` colour map into the `leafId → hex` map the SVG adapter's
 * `setLeafColorMap` consumes. The leaf id is each row's `labelField` value (the
 * column matched to tree leaf labels). Rows with an empty `field` value, or a value
 * absent from `valueColors`, are skipped.
 */
export function buildLeafColorMap(
  metadata: Record<string, unknown>[],
  labelField: string,
  field: string,
  valueColors: Map<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const row of metadata) {
    const value = row[field];
    if (isEmptyValue(value)) continue;
    const colour = valueColors.get(String(value));
    if (colour === undefined) continue;
    out[String(row[labelField])] = colour;
  }
  return out;
}

/** Flatten a `value → hex` map into ordered rows for the static legend. */
export function legendEntries(
  valueColors: Map<string, string> | null | undefined,
): { value: string; colour: string }[] {
  if (!valueColors) return [];
  return [...valueColors.entries()].map(([value, colour]) => ({ value, colour }));
}
